import { GROUND_NAME_MAX, isPrivateGround } from "./groundEdit";

/**
 * Issue #166 Q4 (Martin, 2026-08-27) — the admin venue console's rules.
 *
 * "Admins have the API capability but no edit/archive UI" was #500's own
 * stated gap, and Martin's answer was that API-only is not acceptable for
 * MVP: an admin must be able to edit a venue and archive it from the
 * interface. This module is the page's testable half — this repo mounts no
 * components in unit tests, so a rule written inside JSX is a rule nothing
 * can assert (the `lib/groundEdit.js` / `GroundEditPanel.jsx` split).
 *
 * WHAT IS DELIBERATELY NOT HERE: authorisation. Who may edit is
 * `canOfferGroundEdit` / `isVenueAuthority` (`lib/groundEdit.js`), decided
 * once for every surface — the admin console asks the same predicate the
 * map's detail dialog asks, so the two can never disagree about who gets
 * the affordance. The routes themselves are the boundary
 * (`checkGroundCorrectionGate`, `requireRole("admin")` on the audit read).
 */

/**
 * The list an admin scans: filtered by one query across the three fields a
 * venue is recognised by, sorted by name so the same directory reads the
 * same way on every load. Matching is case-insensitive substring — the
 * directory is hundreds of rows, not millions, and "torv" finding "Torvale
 * Domain" is the whole requirement.
 *
 * ARCHIVED ROWS ARE KEPT, not filtered: this is the management surface,
 * exactly the reader `include_archived=1` exists for (FAC-11) — an admin
 * who cannot see an archived venue cannot restore it.
 */
export function filterVenues(grounds, query) {
  const q = String(query ?? "").trim().toLowerCase();
  const rows = (grounds || []).filter((g) => g && g.ground_id);
  const matched = q
    ? rows.filter((g) =>
        [g.name, g.city, g.country].some((v) => String(v ?? "").toLowerCase().includes(q)),
      )
    : rows;
  return [...matched].sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? "")));
}

/**
 * The ledger's lifecycle rows carry their event in `field` (the repo writes
 * them this way — `created`/`deleted` with the record's name as the value,
 * `merged_into` with the survivor's id). Everything else is a field diff.
 * Named here so the renderer branches on one list, not on prose.
 */
export const AUDIT_LIFECYCLE_FIELDS = ["created", "deleted", "merged_into"];

/** `null` in the ledger means "no value", and the screen must not render it
 * as the word "null" — but "" and NULL are different facts and both honest. */
export function auditValueLabel(value) {
  if (value === null || value === undefined) return "—";
  const text = String(value);
  return text === "" ? "(empty)" : text;
}

/**
 * One ledger row as one sentence fragment. No interpretation, no
 * summarising: the ledger is evidence (FAC-10's retention argument), and
 * this page's job is to show what it holds — who changed what, when — not
 * to editorialise it.
 */
export function describeAuditEntry(entry) {
  if (!entry || !entry.field) return "";
  if (entry.field === "created") return `created "${auditValueLabel(entry.new_value)}"`;
  if (entry.field === "deleted") return `deleted "${auditValueLabel(entry.old_value)}"`;
  if (entry.field === "merged_into") return `merged into ${auditValueLabel(entry.new_value)}`;
  return `${entry.field}: ${auditValueLabel(entry.old_value)} → ${auditValueLabel(entry.new_value)}`;
}

/** "Venue" or the facility's place in the story — the ledger keys every row
 * by the venue and names the record inside it. */
export function auditRecordLabel(entry) {
  return entry?.record_type === "facility" ? "Facility" : "Venue";
}

/**
 * Who did it. `actor_id` is a user id, which is exactly what the ledger
 * holds (Q5: the trail is admin-only precisely because it names actors) —
 * shown as the id rather than resolved to a display name, because the
 * ledger's copy is the durable one and a name lookup that failed or
 * changed since would quietly misattribute the row. NULL is a seeded or
 * system-scripted write, and saying so beats a dash that reads as a bug.
 */
export function auditActorLabel(entry) {
  return entry?.actor_id ? entry.actor_id : "system";
}

/**
 * The timestamp, honestly: the ledger's ISO stamp rendered in the reader's
 * locale, falling back to the raw string when the date cannot parse rather
 * than to a guess. Never relative ("3 days ago") — an audit trail is read
 * to settle disputes, and "when exactly" is the question.
 */
export function auditTimestampLabel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString("en-NZ");
}

/* ==========================================================================
 * Issue #603 (Martin, 2026-09-02) — the directory as a WORKLIST.
 *
 * "Within the Admin Portal under Grounds, can we create filters and sort
 * features to make it more user friendly to manage." The OSM load (#599)
 * puts ~97 venues in this list at once and the follow-up work — naming
 * Auckland's fields and nets, chasing unrated conditions — is list
 * management. One search box does not do that job.
 *
 * ALL OF IT IS CLIENT-SIDE, DELIBERATELY. The directory read already
 * returns every field a filter or a sort here needs (`place_type`,
 * `is_verified`, `archived_at`, `condition_rated_at`, `updated_at`, and the
 * derived `facility_count` — `api/src/routes/grounds.ts`, `toApiGround`),
 * and the page has them all in memory already. So this is a control strip
 * over data fetched regardless: no endpoint, no schema, no change to who
 * may see what. A paged API is the right answer at a size this directory is
 * nowhere near; #603 records the revisit trigger as "when the directory
 * outgrows a single fetch".
 *
 * IT LIVES HERE AND NOT IN THE JSX for the reason at the top of this file:
 * this repo mounts no components in unit tests, so a rule written inside a
 * component is a rule nothing can assert. Every decision below — nulls
 * last, page-1 reset, the clamped page — is a line in `adminVenues.test.js`.
 * ========================================================================== */

/** 100 rows a page, Martin's number. Exported so the page's controls and
 * the tests read the same constant rather than two copies of "100". */
export const VENUE_PAGE_SIZE = 100;

/**
 * Status. Default is ACTIVE — the working set for an admin managing a live
 * directory — but archived is one control away and "All" still shows the
 * mixed list this page showed before #603. FAC-11's requirement is that an
 * archived venue stays REACHABLE so it can be restored, not that it is
 * always on screen; hiding it behind a named filter option satisfies that
 * and stops 97 retired rows padding the worklist.
 */
export const VENUE_STATUS_FILTERS = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
  { value: "all", label: "All statuses" },
];

/**
 * The Auckland worklist, and the reason #603 was filed now: every venue
 * where nobody has entered a field or a net yet. `facility_count` is
 * derived server-side on every read (GND-03), so "none" is a fact about the
 * directory, not about what this page happened to fetch.
 */
export const VENUE_SURFACE_FILTERS = [
  { value: "any", label: "Any surfaces" },
  { value: "none", label: "Surfaces: none" },
];

/** Unrated is `condition_rated_at` NULL. The pair is null together in the
 * engine (GND-04), so the date alone answers it. */
export const VENUE_CONDITION_FILTERS = [
  { value: "any", label: "Any condition" },
  { value: "unrated", label: "Condition: unrated" },
];

/**
 * Provenance. "Verified" and "Community" split on `is_verified` — the queue
 * an admin works.
 *
 * "PRIVATE" IS THE #606 ADDITION, and the comment that used to stand here
 * is why it was needed: it said "every row this page lists is already in the
 * shared directory", and that was false. `GET /api/grounds` also returns the
 * caller's OWN unpublished pins, so an admin who added venues through the
 * portal saw them in this list looking exactly like published ones — 27 of
 * them. This option is the worklist that finds them: `is_global` false AND
 * `is_verified` false, the same predicate the badge on the row uses
 * (`isPrivateGround`), so filter and badge cannot disagree.
 *
 * It overlaps "Community" on purpose rather than being carved out of it:
 * community means unverified, private means unpublished, and a row is
 * routinely both. A filter that silently excluded private rows from
 * "Community" would hide them from the one option an admin already reaches
 * for.
 */
export const VENUE_PROVENANCE_FILTERS = [
  { value: "any", label: "Any provenance" },
  { value: "verified", label: "Verified" },
  { value: "community", label: "Community" },
  { value: "private", label: "Private (unpublished)" },
];

/**
 * The sorts, each with the direction it is USEFUL in.
 *
 * "Recently changed" means newest first and "oldest condition rating" means
 * oldest first — a sort whose name states a direction must arrive in that
 * direction, or the control lies about itself. The toggle still flips any
 * of them; `defaultDirection` is only what you get on picking the sort.
 */
export const VENUE_SORTS = [
  { value: "name", label: "Name", defaultDirection: "asc" },
  { value: "city", label: "City", defaultDirection: "asc" },
  { value: "updated", label: "Recently changed", defaultDirection: "desc" },
  { value: "condition_rated", label: "Oldest condition rating", defaultDirection: "asc" },
  { value: "facility_count", label: "Surface count", defaultDirection: "asc" },
];

/** Name, ascending — what this page has always opened as (#166). A default
 * that changes when a feature lands is a feature that broke the page. */
export const DEFAULT_VENUE_CRITERIA = {
  query: "",
  placeType: "all",
  status: "active",
  surfaces: "any",
  condition: "any",
  provenance: "any",
  sort: "name",
  direction: "asc",
  page: 1,
};

/** The direction a sort opens in; unknown sort falls back to ascending. */
export function venueSortDirection(sort) {
  return VENUE_SORTS.find((s) => s.value === sort)?.defaultDirection ?? "asc";
}

/** Missing counts as zero — the API always sends the number, and a row that
 * somehow lacks it has no recorded surfaces, which is exactly what the
 * worklist is looking for. */
function facilityCountOf(g) {
  const n = Number(g?.facility_count);
  return Number.isFinite(n) ? n : 0;
}

/**
 * One predicate per filter, "any"/"all" meaning NO predicate rather than a
 * predicate that happens to match everything (`guideConsole.js` states the
 * same rule for the same reason: the two differ on junk rows).
 */
function matchesVenueFilters(g, c) {
  if (c.placeType && c.placeType !== "all" && g.place_type !== c.placeType) return false;
  if (c.status === "active" && g.archived_at) return false;
  if (c.status === "archived" && !g.archived_at) return false;
  if (c.surfaces === "none" && facilityCountOf(g) !== 0) return false;
  if (c.condition === "unrated" && g.condition_rated_at) return false;
  if (c.provenance === "verified" && !g.is_verified) return false;
  if (c.provenance === "community" && g.is_verified) return false;
  // #606 — the unpublished worklist. One predicate, shared with the badge.
  if (c.provenance === "private" && !isPrivateGround(g)) return false;
  return true;
}

/**
 * The sort key, or `null` for "this row has nothing to sort by".
 *
 * NULLS LAST, IN BOTH DIRECTIONS — one rule, not a per-sort exception.
 * #603 asks for it on `condition_rated_at` ("they belong to the 'unrated'
 * filter, not the top of this sort") and the same argument holds for a
 * venue with no city: a row that cannot answer the question should not win
 * the ordering it cannot answer. Flipping direction reorders the rows that
 * HAVE a value; it never promotes the ones that don't.
 */
function venueSortKey(g, sort) {
  if (sort === "city") return String(g.city ?? "").trim() || null;
  if (sort === "updated") return g.updated_at || null;
  if (sort === "condition_rated") return g.condition_rated_at || null;
  if (sort === "facility_count") return facilityCountOf(g);
  return String(g.name ?? "").trim() || null;
}

function compareVenueKeys(a, b) {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

/**
 * Sort a name-ordered list by the chosen key. The input is already
 * name-sorted (`filterVenues`) and `Array.prototype.sort` is stable, so
 * every sort here gets NAME as its tiebreak for free — two Auckland venues,
 * two unrated venues, two venues with zero surfaces always come out in the
 * same order, which is what makes paging through them trustworthy.
 */
function sortVenues(rows, sort, direction) {
  const sign = direction === "desc" ? -1 : 1;
  return [...rows].sort((x, y) => {
    const a = venueSortKey(x, sort);
    const b = venueSortKey(y, sort);
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return sign * compareVenueKeys(a, b);
  });
}

/**
 * Has the admin narrowed the list at all? Drives two things that must agree:
 * whether "Clear filters" is offered, and whether the count line says
 * "matching venues" or just "venues".
 */
export function isVenueCriteriaNarrowed(criteria) {
  const c = { ...DEFAULT_VENUE_CRITERIA, ...(criteria || {}) };
  return (
    String(c.query ?? "").trim() !== "" ||
    c.placeType !== DEFAULT_VENUE_CRITERIA.placeType ||
    c.status !== DEFAULT_VENUE_CRITERIA.status ||
    c.surfaces !== DEFAULT_VENUE_CRITERIA.surfaces ||
    c.condition !== DEFAULT_VENUE_CRITERIA.condition ||
    c.provenance !== DEFAULT_VENUE_CRITERIA.provenance
  );
}

/**
 * Merge a change into the criteria. **Any change to what is being LOOKED
 * FOR resets to page 1** — search, filter or sort. Without that, narrowing
 * the list while on page 3 leaves an admin staring at an empty page that is
 * indistinguishable from "no venues match", which is the paging bug every
 * list makes once.
 *
 * A patch that only moves the page is the one exception, for the obvious
 * reason. Picking a new sort also takes that sort's own direction, unless
 * the patch names a direction itself.
 */
export function applyVenueCriteria(criteria, patch) {
  const base = { ...DEFAULT_VENUE_CRITERIA, ...(criteria || {}) };
  const next = { ...base, ...(patch || {}) };
  const keys = Object.keys(patch || {});
  const pageOnly = keys.length > 0 && keys.every((k) => k === "page");
  if (patch && "sort" in patch && !("direction" in patch)) {
    next.direction = venueSortDirection(next.sort);
  }
  if (!pageOnly) next.page = 1;
  return next;
}

/**
 * Clear the search and every filter, keeping the SORT and returning to page
 * 1. Both "Clear filters" affordances — the one on the control strip and
 * the one in the no-matches empty state — go through this, so they cannot
 * come to mean two different things. The sort survives on purpose: an
 * ordering is a way of reading the list, not a thing you are stuck behind.
 */
export function clearVenueFilters(criteria) {
  const c = { ...DEFAULT_VENUE_CRITERIA, ...(criteria || {}) };
  return applyVenueCriteria(c, {
    query: "",
    placeType: DEFAULT_VENUE_CRITERIA.placeType,
    status: DEFAULT_VENUE_CRITERIA.status,
    surfaces: DEFAULT_VENUE_CRITERIA.surfaces,
    condition: DEFAULT_VENUE_CRITERIA.condition,
    provenance: DEFAULT_VENUE_CRITERIA.provenance,
  });
}

/* ==========================================================================
 * Issue #616 (Martin, 2026-09-04) — THE DEEP LINK INTO ONE VENUE'S RECORD.
 *
 * "When I look at the possible duplicate grounds. I cannot find some of
 * these — for example Sablemere Cricket Club is not showing up as a ground when
 * I search for it."
 *
 * Name matching was never the problem: `filterVenues` above is a
 * case-insensitive substring over name, city and country, and "Sablemere
 * Cricket Club" matches "Sablemere Cricket Club (Counties Manukau)". What hid
 * the row was a FILTER still set from another task — Provenance "Private
 * (unpublished)" (#606), which every ground on the duplicate report is
 * guaranteed not to match because that report only ever lists PUBLIC
 * grounds, and Status "Active" (#603's default), which hides an archived
 * venue. Neither has anything to do with the pair the reviewer was sent to
 * look at, and nothing on the destination says a filter is why the row is
 * missing.
 *
 * So the report links to the RECORD, and the link arrives at a list that
 * cannot be hiding it. The two halves of that are:
 *
 *   `venueRecordPath` — the ONE place a link into this page is built.
 *   Both the report's two links and this page's own reader of the param
 *   go through `VENUE_FOCUS_PARAM`, so the sender and the receiver cannot
 *   drift apart into a link that silently lands on an unfocused list.
 *
 *   `focusVenueCriteria` — the criteria a followed link lands on. Not
 *   `clearVenueFilters`: that returns to the DEFAULTS, and the default
 *   status is "active", so following a link to an archived venue would
 *   land on a list still hiding it. This widens every filter to the
 *   setting that excludes nothing, and then puts the reader on the page
 *   the row is actually on — paging is the third way a row can be present
 *   and invisible, and a deep link that lands on page 1 of 4 has not shown
 *   anybody anything.
 * ========================================================================== */

/** The query parameter `/admin/venues` reads a focused ground id from. */
export const VENUE_FOCUS_PARAM = "ground";

/**
 * A link straight to one venue's record. Built here rather than at each
 * call site so the parameter name lives in exactly one place — the page
 * that reads it imports the same constant.
 */
export function venueRecordPath(groundId) {
  const id = String(groundId ?? "").trim();
  if (!id) return "/admin/venues";
  return `/admin/venues?${VENUE_FOCUS_PARAM}=${encodeURIComponent(id)}`;
}

/**
 * The criteria a followed deep link lands on: nothing narrowing, and the
 * page the row is on.
 *
 * The SORT is kept, like `clearVenueFilters` keeps it — an ordering is a way
 * of reading the list, not a thing the reader is stuck behind, and it does
 * not hide anything. Everything that CAN hide a row is widened: the search
 * box, all five filters, and (via `selectVenues`) the page.
 *
 * A ground id that is not in the directory yields page 1 rather than
 * throwing or guessing — the page says so on screen instead, because "that
 * venue is not in this list" is a fact worth stating and an empty
 * unexplained list is not.
 */
export function focusVenueCriteria(criteria, grounds, groundId) {
  const widened = applyVenueCriteria(criteria, {
    query: "",
    placeType: "all",
    // NOT `DEFAULT_VENUE_CRITERIA.status`, which is "active" — see above.
    status: "all",
    surfaces: "any",
    condition: "any",
    provenance: "any",
  });
  const id = String(groundId ?? "").trim();
  if (!id) return widened;
  return { ...widened, page: venuePageContaining(grounds, widened, id) };
}

/**
 * The 1-based page a ground falls on under these criteria, or 1 when it is
 * not in the list at all. Shares `selectVenues`'s own filter, sort and page
 * size by asking it for the whole list rather than restating any of them —
 * a second copy of the ordering here is a copy that disagrees the first time
 * a sort is added.
 */
export function venuePageContaining(grounds, criteria, groundId) {
  const id = String(groundId ?? "").trim();
  if (!id) return 1;
  const index = matchingVenueRows(grounds, criteria).findIndex((g) => g.ground_id === id);
  if (index < 0) return 1;
  return Math.floor(index / VENUE_PAGE_SIZE) + 1;
}

/**
 * Every row matching the criteria, searched, filtered and sorted — the whole
 * list before it is cut into pages.
 *
 * Extracted (#616) so `selectVenues` and `venuePageContaining` cannot come
 * to disagree about which rows are in the list or what order they are in: a
 * deep link that lands on the page a SECOND copy of that rule computed is a
 * link to the wrong page, and it would be wrong only for the directory sizes
 * nobody tests with.
 */
function matchingVenueRows(grounds, criteria, { keepIds } = {}) {
  const c = { ...DEFAULT_VENUE_CRITERIA, ...(criteria || {}) };
  const kept = keepIds instanceof Set ? keepIds : new Set(keepIds || []);
  const all = (grounds || []).filter((g) => g && g.ground_id);
  const searched = filterVenues(all, c.query);
  const filtered = searched.filter((g) => kept.has(g.ground_id) || matchesVenueFilters(g, c));
  return sortVenues(filtered, c.sort, c.direction);
}

/**
 * THE function the page renders from: the whole directory in, one page of
 * rows out, plus every number the page has to state honestly.
 *
 * `matching` and `total` are BOTH returned on purpose. "Showing 1–100 of
 * 97 matching venues" is only true if the 97 is the filtered count, and the
 * empty state can only tell "the directory is empty" from "nothing matches
 * these filters" by holding both. A page that knows one number tells the
 * wrong story with it.
 *
 * The page is CLAMPED rather than trusted: a stale page number (criteria
 * changed by another path, a restored state, a bug here later) yields the
 * last real page, never a blank screen with rows sitting behind it.
 *
 * `keepIds` (issue #613) — ROWS THAT STAY THROUGH A CHANGE THEY CAUSED.
 * Publishing a venue from the "Private (unpublished)" worklist changes the
 * very field that filter selects on, so on the next read the row an admin
 * just acted on stops matching and vanishes out from under their finger,
 * with nothing left on screen to say the write landed. These ids are
 * exempt from the FILTERS — never from the search, which is a question the
 * reader is actively typing — so the row holds its sorted position and
 * shows its new state until the ids are dropped, which `AdminVenues` does
 * on the next change to what is being looked for.
 *
 * They ARE counted in `matching`, because the count line describes what is
 * on screen and a kept row is on screen: a count that excluded them would
 * say "27 matching venues" above 28 rows.
 */
export function selectVenues(grounds, criteria, { keepIds } = {}) {
  const c = { ...DEFAULT_VENUE_CRITERIA, ...(criteria || {}) };
  const all = (grounds || []).filter((g) => g && g.ground_id);
  const sorted = matchingVenueRows(all, c, { keepIds });

  const matching = sorted.length;
  const pageCount = Math.max(1, Math.ceil(matching / VENUE_PAGE_SIZE));
  const page = Math.min(Math.max(Math.trunc(Number(c.page) || 1), 1), pageCount);
  const start = (page - 1) * VENUE_PAGE_SIZE;
  const rows = sorted.slice(start, start + VENUE_PAGE_SIZE);

  return {
    rows,
    matching,
    total: all.length,
    page,
    pageCount,
    pageSize: VENUE_PAGE_SIZE,
    from: matching === 0 ? 0 : start + 1,
    to: start + rows.length,
  };
}

/**
 * The count line. States the range AND the total every time — #603's
 * requirement, because "97 venues" under a filter reads as the size of the
 * directory unless the sentence says otherwise. "matching" appears only
 * when something is actually narrowing the list, so an unfiltered console
 * does not imply a filter is on.
 */
export function describeVenuePage(result, criteria) {
  const noun = result?.matching === 1 ? "venue" : "venues";
  const qualified = isVenueCriteriaNarrowed(criteria) ? `matching ${noun}` : noun;
  if (!result || result.matching === 0) return `No ${qualified}`;
  return `Showing ${result.from}–${result.to} of ${result.matching} ${qualified}`;
}

/* ==========================================================================
 * Issue #612 (Martin, 2026-09-03) — SELECT MANY, PUBLISH ONCE, AND BE WARNED
 * FIRST.
 *
 * Martin is hand-entering grounds from council published documents and says
 * he will keep doing it. #606 gave him a Publish button per row; at 27 venues
 * that is tedious and survivable, at council-list scale it is the thing that
 * makes a good process get abandoned.
 *
 * Everything below is a RULE, not a widget, and it lives here for this
 * module's own reason: this repo mounts no components in unit tests, so a
 * rule written inside JSX is a rule nothing can assert. The page holds a
 * `Set` of ids and renders what these functions say about it.
 *
 * THREE RULES SHAPE THE SELECTION, and each has a failure it is avoiding:
 *
 *   1. ONLY A PUBLISHABLE ROW IS SELECTABLE. `canPublishGround` decides —
 *      the same shared predicate the row's own Publish button rides (#606),
 *      never a second copy. A checkbox on a published venue is a checkbox
 *      with no verb behind it, and a batch that silently contained four of
 *      them would report four `already_published` rows an admin never chose.
 *
 *   2. THE SELECTION IS PRUNED TO WHAT IS ON SCREEN. Publishing is one-way
 *      and there is no unpublish (`setGroundPublished`), so the count on the
 *      confirm has to be the count of rows the admin can actually see. A
 *      selection that survived a filter change would let "Publish 40" mean
 *      forty venues thirty of which scrolled out of the reader's life two
 *      filters ago. `AdminVenues` clears the set on every criteria change;
 *      `pruneVenueSelection` is the belt for anything that changes underneath
 *      it — a reload, a row published by the other affordance.
 *
 *   3. THE DUPLICATE WARNING CANNOT BECOME A BLOCK. #612 is explicit: warn,
 *      do not block, because the admin is the reviewer and may legitimately
 *      publish a near-name match. So `canConfirmBulkPublish` asks ONE
 *      question — is anything selected — and the preflight is not one of its
 *      inputs. A preflight that found ten duplicates, and a preflight that
 *      failed to run at all, both leave the button pressable. That is the
 *      point: a warning that can stop the write is a block wearing a
 *      warning's copy.
 * ========================================================================== */

/**
 * The ids on THIS page that may be published, in the page's own order.
 *
 * Rows come from `selectVenues`, so this is a page at a time — deliberately.
 * "Select all" that reached across pages would arm a batch out of rows the
 * admin never saw, and the API caps a batch at 100 for the same reason the
 * page shows 100.
 */
export function selectableVenueIds(rows, canPublish) {
  return (rows || []).filter((g) => g && g.ground_id && canPublish(g)).map((g) => g.ground_id);
}

/** Add or remove one id. Returns a NEW Set — React state, not a mutation. */
export function toggleVenueSelection(selected, id) {
  const next = new Set(selected || []);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/**
 * Select every publishable row on the page, or clear them — one control, so
 * the header checkbox cannot come to disagree with the rows beneath it.
 * "All selected" means every selectable row is in the set; pressing it then
 * removes exactly those, leaving anything selected elsewhere alone.
 */
export function toggleAllVenueSelection(selected, ids) {
  const next = new Set(selected || []);
  const all = (ids || []).length > 0 && (ids || []).every((id) => next.has(id));
  for (const id of ids || []) {
    if (all) next.delete(id);
    else next.add(id);
  }
  return next;
}

/** Are all the selectable rows on this page selected? Drives the header
 * control's own checked state, from the same list it toggles. */
export function allVenuesSelected(selected, ids) {
  return (ids || []).length > 0 && (ids || []).every((id) => (selected || new Set()).has(id));
}

/**
 * Drop every id that is no longer a publishable row in `grounds` — rule 2.
 * Returns the SAME Set when nothing changed, so React does not re-render on
 * every reload for no reason.
 */
export function pruneVenueSelection(selected, grounds, canPublish) {
  const set = selected || new Set();
  const live = new Set(selectableVenueIds(grounds || [], canPublish));
  const kept = [...set].filter((id) => live.has(id));
  if (kept.length === set.size) return set;
  return new Set(kept);
}

/**
 * The confirm sentence. States the COUNT and the one-way fact, because #612
 * requires both: "one confirm for the batch" is only safe if the confirm says
 * how big the batch is, and publishing cannot be taken back.
 */
export function describeBulkPublishConfirm(count) {
  const n = Number(count) || 0;
  const noun = n === 1 ? "venue" : "venues";
  return `Publish ${n} ${noun} to the shared directory? Everyone will see ${n === 1 ? "it" : "them"}, and there is no unpublish.`;
}

/**
 * Is the Confirm button pressable? RULE 3, written as a function so it can be
 * asserted rather than promised.
 *
 * The only input is how many venues are selected. Not the duplicates, not
 * whether the preflight succeeded, not whether it ran. A test that passes a
 * preflight full of duplicates and a failed preflight through here and still
 * gets `true` is the proof that the warning is a warning.
 */
export function canConfirmBulkPublish(count) {
  return (Number(count) || 0) > 0;
}

/**
 * What the preflight found, folded into the three numbers a confirm shows.
 *
 * `duplicates` is the per-venue list, kept whole rather than counted: "3 of
 * the 12 may already be in the directory" is not actionable, and the whole
 * risk #612 names is a specific park already sitting there under a slightly
 * different name. The reader needs the names.
 *
 * `failed` is a first-class state, not an absence. A preflight that could not
 * run must SAY so — a confirm that silently shows no warnings after a failed
 * check reads as a clean bill of health, which is the worst thing this panel
 * could say.
 */
export function summarisePreflight(preflight, { failed = false } = {}) {
  if (failed || !preflight) {
    return { failed: true, checked: 0, withDuplicates: [], blocked: [] };
  }
  const rows = preflight.grounds || [];
  return {
    failed: false,
    checked: rows.length,
    withDuplicates: rows.filter((g) => (g.duplicates || []).length > 0),
    blocked: rows.filter((g) => g.code),
  };
}

/** The warning line, or "" when there is nothing to warn about. Never a
 * refusal — see `canConfirmBulkPublish`. */
export function describeDuplicateWarning(summary) {
  if (!summary || summary.failed) {
    return "The duplicate check could not run. You can still publish — nothing here blocks it.";
  }
  const n = summary.withDuplicates.length;
  if (n === 0) return "";
  return n === 1
    ? "1 of these looks like a venue already in the directory. Publish anyway if it is a different place."
    : `${n} of these look like venues already in the directory. Publish anyway if they are different places.`;
}

/**
 * The batch's own report, from the API's per-row results.
 *
 * FAILURES ARE KEPT AS ROWS, with the venue's name and the reason the API
 * wrote for this reader. #612's requirement is that a partial failure "names
 * the rows that failed and keeps the successes"; a count of failures names
 * nobody, and "something went wrong" is the exact sentence the ticket rules
 * out.
 */
export function summariseBulkPublish(body) {
  const results = body?.results || [];
  const summary = body?.summary || {};
  return {
    requested: Number(summary.requested) || results.length,
    published: Number(summary.published) || 0,
    alreadyPublished: Number(summary.already_published) || 0,
    failures: results
      .filter((r) => r && r.status === "failed")
      .map((r) => ({
        ground_id: r.ground_id,
        // An unnamed row is one the caller cannot see — the API withholds the
        // name on purpose there (it must not become an enumeration oracle),
        // so the screen says what it honestly knows.
        name: r.name || "This venue",
        detail: r.detail || "This venue could not be published.",
      })),
    publishedIds: results.filter((r) => r && r.status === "published").map((r) => r.ground_id),
    // Rows the batch did not move, so the next press retries exactly them.
    failedIds: results.filter((r) => r && r.status === "failed").map((r) => r.ground_id),
  };
}

/**
 * The result sentence. Says what landed AND what did not, in one line, always
 * both — a "Published 38" that omits the two failures is the "something went
 * wrong" defect with the sign flipped.
 */
export function describeBulkPublishResult(summary) {
  if (!summary) return "";
  const parts = [`Published ${summary.published} of ${summary.requested}`];
  if (summary.alreadyPublished > 0) {
    parts.push(`${summary.alreadyPublished} already published`);
  }
  if (summary.failures.length > 0) {
    parts.push(`${summary.failures.length} could not be published`);
  }
  return `${parts.join(" · ")}.`;
}

/* ==========================================================================
 * ISSUE #627 — THE BULK IMPORTER'S VOCABULARY, ON THIS SIDE OF THE WIRE.
 *
 * Martin could not use Bulk import for an evening because Preview answered
 * `Internal server error` — no row, no reason, no way to tell whether the
 * problem was his file, its size, its delimiter or the platform. The API now
 * answers per row (`check_failed`, `write_failed`, and a named refusal over
 * the row cap). These are the sentences that turn those codes into something
 * an admin can act on, and they live here rather than in the panel for the
 * house reason: this repo mounts no components in unit tests, so a rule
 * written in JSX is a rule nobody can assert.
 * ========================================================================== */

/** The pill on each row. `check_failed` and `write_failed` are deliberately
 * NOT "Rejected": telling an admin their row was refused when the server
 * could not check or write it sends them to edit a file that is fine. */
export const IMPORT_STATUS_LABELS = {
  will_create: "Will create",
  matches_existing: "Matches existing",
  rejected: "Rejected",
  check_failed: "Not checked",
  write_failed: "Not written",
  skipped: "Skipped",
};

export function importStatusLabel(status) {
  return IMPORT_STATUS_LABELS[status] || IMPORT_STATUS_LABELS.skipped;
}

/**
 * Issue #645 — the condition-notes cap (`CONDITION_NOTES_MAX_LENGTH`,
 * `api/src/db/repos/grounds.ts:71`), mirrored the way `GROUND_NAME_MAX`
 * mirrors `GROUND_NAME_MAX_LENGTH`. Unlike a rejected name, the row's raw
 * notes text never leaves the server (`toApiImportGround` does not send
 * it), so this message can state the bound but not the row's actual count.
 */
const CONDITION_NOTES_MAX = 1000;

/** Reasons with no value attached. */
const IMPORT_REASONS = {
  duplicate_check_failed: "This row could not be checked against the directory — the row itself may be fine.",
  ground_check_failed: "The ground this belongs to could not be checked.",
  write_failed: "This venue could not be written to the directory.",
  ground_write_failed: "The ground this belongs to could not be written.",
  ground_rejected: "The ground this belongs to was rejected.",
  ground_matches_existing: "The ground is already in the directory.",
  condition_notes_too_long: `The condition notes are longer than ${CONDITION_NOTES_MAX} characters, the limit.`,
  half_pin: "A pin needs both a latitude and a longitude, or neither.",
  invalid_latitude: "The latitude is missing or out of range.",
  invalid_longitude: "The longitude is missing or out of range.",
  grounds_csv_empty: "The grounds CSV has no rows.",
};

/** Reasons that carry a value after a colon. */
const IMPORT_REASON_PREFIXES = {
  missing_field: (v) => `The “${v}” column is empty on this row.`,
  invalid_place_type: (v) => `“${v}” is not a place type this platform knows.`,
  invalid_kind: (v) => `“${v}” is not a surface kind this platform knows.`,
  invalid_surface: (v) => `“${v}” is not a surface this platform knows.`,
  invalid_condition: (v) => `“${v}” is not a condition rating this platform knows.`,
  invalid_boolean: (v) => `The “${v}” column must be true or false.`,
  duplicate_ref: (v) => `The ref “${v}” is used by an earlier row.`,
  unknown_ground_ref: (v) => `No ground in this upload has the ref “${v}”.`,
  grounds_csv_missing_header: (v) => `The grounds CSV has no “${v}” column.`,
  facilities_csv_missing_header: (v) => `The fields & nets CSV has no “${v}” column.`,
};

/**
 * One machine reason as a sentence. The raw code is RETURNED UNCHANGED when
 * nothing maps it — a reason the screen cannot phrase is still a reason, and
 * swallowing it would put this panel back where the ticket found it.
 *
 * Issue #645 — `name_too_long` is handled before the static table because it
 * is the one reason whose sentence needs a NUMBER that isn't baked into the
 * message: the row's own name is still on `row.name` (`toApiImportGround`
 * sends it back unconditionally, rejected or not), so the actual character
 * count can be named next to the limit instead of a bare code. `row` is
 * optional — the file-level callers (`fileErrors`) have no row — and this
 * degrades to naming only the limit if the name is missing.
 */
export function describeImportReason(reason, row) {
  if (!reason) return "";
  const code = String(reason);
  if (code === "name_too_long") {
    const length = String(row?.name ?? "").trim().length;
    return `Name is ${length} character${length === 1 ? "" : "s"}; the limit is ${GROUND_NAME_MAX} — ` +
      "long names are capped so one oversized name cannot break the duplicate check for the whole file.";
  }
  if (IMPORT_REASONS[code]) return IMPORT_REASONS[code];
  const at = code.indexOf(":");
  if (at > 0) {
    const build = IMPORT_REASON_PREFIXES[code.slice(0, at)];
    if (build) return build(code.slice(at + 1));
  }
  return code;
}

/**
 * Issue #647 — one `matches[]` entry as the sentence an admin needs to judge
 * whether the match is correct, without opening the network tab: the
 * matched ground's name and city. No `country` here — `toApiImportGround`
 * does not send one for a match.
 */
export function describeImportMatch(match) {
  if (!match) return "";
  const name = String(match.name ?? "").trim();
  const city = String(match.city ?? "").trim();
  return city ? `${name} — ${city}` : name;
}

/**
 * What a `matches_existing` row MEANS, in words — the row-level echo of the
 * sentence `describeImportBlock` already gives at the file level, so colour
 * is never the only signal a row was matched rather than rejected (#647).
 */
export function describeImportMatchStatus() {
  return "Already in the directory — nothing will be created for this row.";
}

/** Distinct matched-ground names across every `matches_existing` row in a
 * preview, for the file-level "nothing would be created" sentence (#647) —
 * so a file that creates nothing says what it collided with, not just that
 * it collided. */
function importMatchedGroundNames(grounds) {
  const seen = new Set();
  for (const g of grounds || []) {
    if (!g || g.status !== "matches_existing") continue;
    for (const m of g.matches || []) {
      const name = String(m?.name ?? "").trim();
      if (name) seen.add(name);
    }
  }
  return [...seen];
}

/**
 * The preview, folded into what the panel decides from.
 *
 * `unchecked` is the ROWS, not a count, for `summariseBulkPublish`'s reason:
 * "3 rows could not be checked" names nobody, and the admin's next move is
 * to look at those lines.
 */
export function summariseImportPreview(preview) {
  const summary = preview?.summary || {};
  const grounds = preview?.grounds || [];
  return {
    willCreate: Number(summary.grounds_will_create) || 0,
    matchExisting: Number(summary.grounds_match_existing) || 0,
    rejected: Number(summary.grounds_rejected) || 0,
    unchecked: grounds.filter((g) => g && g.status === "check_failed"),
    fileErrors: preview?.file_errors || [],
  };
}

/** The dry-run line. Always says the unchecked rows if there are any — a
 * summary that counts only the clean verdicts is the false-clean-bill the
 * bulk publish panel already refuses to give (`describeDuplicateWarning`). */
export function describeImportPreview(summary) {
  if (!summary) return "";
  const head = `Dry run — ${summary.willCreate} to create, ${summary.matchExisting} already in the directory, ${summary.rejected} rejected.`;
  if (summary.unchecked.length === 0) return head;
  const lines = summary.unchecked.map((g) => g.line).join(", ");
  const noun = summary.unchecked.length === 1 ? "row" : "rows";
  return `${head} ${summary.unchecked.length} ${noun} could not be checked (line${summary.unchecked.length === 1 ? "" : "s"} ${lines}).`;
}

/**
 * Is Confirm pressable? A clean preview, something to create, a source
 * named — and NO unchecked row.
 *
 * That last clause is not belt-and-braces: `confirm` refuses the whole file
 * (422 `rows_unchecked`) when any row's duplicate check failed, because a
 * row nobody could check might already be in a directory that has no delete.
 * The button reflects the API's decision instead of discovering it.
 */
export function canConfirmImport({ preview, source } = {}) {
  if (!preview) return false;
  const summary = summariseImportPreview(preview);
  return (
    summary.fileErrors.length === 0 &&
    summary.unchecked.length === 0 &&
    summary.willCreate > 0 &&
    String(source || "").trim().length > 0
  );
}

/** Why Confirm is off, or "" when it is on (or nothing has been previewed). */
export function describeImportBlock({ preview, source } = {}) {
  if (!preview) return "";
  const summary = summariseImportPreview(preview);
  if (summary.fileErrors.length > 0) return "Fix the file problems above, then preview again.";
  if (summary.unchecked.length > 0) {
    const lines = summary.unchecked.map((g) => g.line).join(", ");
    return `Line ${lines} could not be checked against the directory. Preview again — nothing imports until every row has a verdict.`;
  }
  if (summary.willCreate === 0) {
    const matched = importMatchedGroundNames(preview.grounds);
    return matched.length > 0
      ? `Nothing in this file would be created — every row matched a ground already in the directory: ${matched.join(", ")}.`
      : "Nothing in this file would be created.";
  }
  if (String(source || "").trim().length === 0) return "Name a source to enable Confirm.";
  return "";
}

/** What a finished import did, including what it could not do. Mirrors
 * `describeBulkPublishResult`: never a success line that omits the failures. */
export function describeImportResult(result) {
  if (!result) return "";
  const summary = result.summary || {};
  const created = (result.created_ground_ids || []).length;
  const parts = [`Imported ${created} venue(s) and ${result.created_facilities || 0} surface(s)`];
  if (Number(summary.grounds_match_existing) > 0) {
    parts.push(`${summary.grounds_match_existing} already in the directory`);
  }
  if (Number(summary.grounds_write_failed) > 0) {
    parts.push(`${summary.grounds_write_failed} could not be written`);
  }
  return `${parts.join(" · ")}.`;
}
